'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'

import ResetIcon from '@/assets/icons/leaderboard/lb-cross-16.svg'
import { AthletesBoard } from '@/features/athletes-leaderboard/components/AthletesBoard'
import { normalizeLeaderboardSportCode } from '@/features/leaderboard/buildLeaderboardSportTabs'
import { LeaderboardEmptyState } from '@/features/leaderboard/components/LeaderboardEmptyState'
import { LeaderboardSearch } from '@/features/leaderboard/components/LeaderboardSearch'
import MultiFilterDropdown from '@/features/leaderboard/components/MultiFilterDropdown'
import { useSportTypes } from '@/features/sport-types/hooks'
import countries from '@/ui/countries.json'

import S from './GlobalAthletesLeaderboard.module.scss'

const SEARCH_DEBOUNCE_MS = 300

// BAT-1217: the week / month filter is planned, not in the scope yet — the
// board is all time (the API keeps the period).
const PERIOD = 'all'

/**
 * BAT-1110 (US-ATH-18.1–18.3): the athletes board — search by Display Name,
 * several sports (searchable, the athletes' list) and countries, «Reset
 * all». Its state lives here: leaving the tab clears the search.
 */
const GlobalAthletesLeaderboard = () => {
  const t = useTranslations('athletesLeaderboard')
  const tb = useTranslations('leaderboardBoard')
  const locale = useLocale()
  const { data: sportTypes = [] } = useSportTypes('athletes_leaderboard')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sports, setSports] = useState<string[]>([])
  const [countryCodes, setCountryCodes] = useState<string[]>([])

  useEffect(() => {
    const timer = setTimeout(
      () => setSearch(searchInput.trim()),
      SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
  }, [searchInput])

  const sportOptions = useMemo(
    () =>
      sportTypes.map(sport => ({
        value: normalizeLeaderboardSportCode(sport.code),
        label: sport.name
      })),
    [sportTypes]
  )

  // The board takes ISO codes; the names follow the page language.
  const countryOptions = useMemo(() => {
    let names: Intl.DisplayNames | null = null
    try {
      names = new Intl.DisplayNames([locale], { type: 'region' })
    } catch {
      names = null
    }
    return (countries as Array<{ name: string; code: string }>)
      .map(country => ({
        value: country.code.toUpperCase(),
        label: names?.of(country.code.toUpperCase()) ?? country.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label, locale))
  }, [locale])

  const hasActiveFilters =
    Boolean(searchInput.trim()) || sports.length > 0 || countryCodes.length > 0

  const resetAll = () => {
    setSearchInput('')
    setSearch('')
    setSports([])
    setCountryCodes([])
  }

  return (
    <div className={S.root}>
      <div className={S.toolbar}>
        <LeaderboardSearch
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('search')}
          clearLabel={tb('clearSearch')}
        />
        <MultiFilterDropdown
          values={sports}
          onChange={setSports}
          options={sportOptions}
          allLabel={tb('allSports')}
          label={tb('sportFilter')}
          searchPlaceholder={t('searchSport')}
          noMatchesLabel={t('noMatches')}
        />
        <MultiFilterDropdown
          values={countryCodes}
          onChange={setCountryCodes}
          options={countryOptions}
          allLabel={t('allCountries')}
          label={t('countryFilter')}
          searchPlaceholder={t('searchCountry')}
          noMatchesLabel={t('noMatches')}
        />
        {hasActiveFilters ? (
          <button type="button" className={S.reset} onClick={resetAll}>
            <ResetIcon className={S.resetIcon} aria-hidden />
            {t('resetAll')}
          </button>
        ) : null}
      </div>

      <AthletesBoard
        scope={{
          sportTypes: sports,
          countries: countryCodes,
          period: PERIOD,
          search: search || undefined
        }}
        // US-ATH-18.3: an empty selection says only «Nothing found»; the
        // query and the filters stay as they are.
        emptyState={<LeaderboardEmptyState title={t('nothingFound')} />}
      />
    </div>
  )
}

export default GlobalAthletesLeaderboard
